/**
 * Google Drive API utilities for exporting lesson plans
 * Uses backend APIs for security
 */

/**
 * Request Google Drive API access token
 * Uses Google Identity Services to get access token with Drive scopes
 */
export const getGoogleAuthToken = async () => {
  return new Promise((resolve, reject) => {
    if (!window.google) {
      reject(new Error('Google Identity Services not loaded. Please sign in first.'));
      return;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      reject(new Error('Google Client ID not configured'));
      return;
    }

    // Request access token with Drive scope only
    window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/drive.file',
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response.access_token);
        }
      },
    }).requestAccessToken();
  });
};

/**
 * Export lesson plan to Google Docs
 * Uses backend API for security
 */
export const exportToGoogleDocs = async (title, content) => {
  try {
    // Get access token from user
    const accessToken = await getGoogleAuthToken();
    
    // Get auth token for backend API
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      throw new Error('Not authenticated. Please sign in.');
    }
    
    // Call backend API
    const response = await fetch('/api/drive/export-to-docs/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${authToken}`,
      },
      body: JSON.stringify({
        access_token: accessToken,
        title: title || 'Untitled Lesson Plan',
        content: content,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to export to Google Docs');
    }
    
    const result = await response.json();
    
    // Open the document in a new tab
    if (result.url) {
      window.open(result.url, '_blank');
    }
    
    return result;
  } catch (error) {
    console.error('Error exporting to Google Docs:', error);
    throw error;
  }
};



/**
 * Save PDF to Google Drive
 * Uses backend API for security
 */
export const savePDFToGoogleDrive = async (title, pdfBlob) => {
  try {
    // Get access token from user
    const accessToken = await getGoogleAuthToken();
    
    // Get auth token for backend API
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      throw new Error('Not authenticated. Please sign in.');
    }
    
    // Convert blob to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });
    
    // Call backend API
    const response = await fetch('/api/drive/save-pdf/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${authToken}`,
      },
      body: JSON.stringify({
        access_token: accessToken,
        title: title || 'Untitled Lesson Plan',
        pdf_base64: base64,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save PDF to Google Drive');
    }
    
    const result = await response.json();
    
    // Open the file in a new tab
    if (result.url) {
      window.open(result.url, '_blank');
    }
    
    return result;
  } catch (error) {
    console.error('Error saving PDF to Google Drive:', error);
    throw error;
  }
};


